"use client";

import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { StaysIcon, FurnishIcon, ExperiencesIcon } from "./NavIcons";
import { useHideOnScrollDown } from "@/lib/hooks/useHideOnScrollDown";

/**
 * Phone-only row of the three section tabs (Stays · Furnish · Experiences)
 * under the header bar, so the three businesses are visible on every page
 * without opening the drawer. The current section is underlined. The row
 * slides away while scrolling down and returns on scroll up, so it does not
 * permanently eat 44px of a small screen. Desktop has the same three tabs in
 * the header itself (Header.tsx), so this is `md:hidden`.
 *
 * The row hangs below the sticky header (absolute, top-full) and slides with a
 * transform, so showing/hiding it never changes the header's height. It used to
 * collapse max-height inside the header, which shifted the whole page 44px on
 * every scroll-direction change: the "jumpy header" on iPhone (2026-10-17).
 * The space it covers at the top of the page is held by SectionTabsSpacer.
 */
export default function SectionTabs() {
  const t = useTranslations("nav");
  const pathname = usePathname();
  // On /search the header comes back on scroll up but this row stays away until
  // the top: returning with it covered the sticky search bar (top-[70px]).
  // There it also hides from 40px (the spacer's height): from there the sticky search bar is stuck under the header.
  const onSearch = pathname.startsWith("/search");
  const hidden = useHideOnScrollDown(onSearch ? 40 : 80, onSearch);
  // /checkout is a focused page: no section switching mid-payment, and the row
  // sliding back in on every scroll-up covered the card icons and the receipt
  // (Maged, 2026-10-06). The WhatsApp bubble and cookie banner are hidden there too.
  if (pathname.startsWith("/checkout")) return null;
  // Stays is the default section: the homepage, search and listings all belong to it.
  // /rides has no tab (it is reached from the homepage section), so nothing is underlined there.
  const section = pathname.startsWith("/furnish") ? "furnish" : pathname.startsWith("/experiences") ? "experiences" : pathname.startsWith("/rides") ? "rides" : "stays";

  const tab = "flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors";
  const on = "text-ss-text border-ss-text";
  const off = "text-ss-muted border-transparent";

  return (
    <div
      className={`md:hidden absolute top-full inset-x-0 -z-10 border-b border-ss-line bg-white transition-[transform,opacity] duration-200 motion-reduce:transition-none ${
        hidden ? "-translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100"
      }`}
      aria-hidden={hidden}
    >
      <nav className="flex px-2" aria-label={t("sectionsAria")}>
        <Link href="/search" aria-current={section === "stays" ? "page" : undefined} className={`${tab} ${section === "stays" ? on : off}`} tabIndex={hidden ? -1 : 0}>
          <StaysIcon />
          {t("stays")}
        </Link>
        <Link href="/furnish" aria-current={section === "furnish" ? "page" : undefined} className={`${tab} ${section === "furnish" ? on : off}`} tabIndex={hidden ? -1 : 0}>
          <FurnishIcon />
          {t("furnish")}
        </Link>
        <Link href="/experiences" aria-current={section === "experiences" ? "page" : undefined} className={`${tab} ${section === "experiences" ? on : off}`} tabIndex={hidden ? -1 : 0}>
          <ExperiencesIcon />
          {t("experiences")}
        </Link>
      </nav>
    </div>
  );
}

/** Holds the tab row's height in the page flow at the very top (phone only), so the
 *  row does not cover the first 42px of the page. It scrolls away with the content. */
export function SectionTabsSpacer() {
  const pathname = usePathname();
  if (pathname.startsWith("/checkout")) return null;
  return <div className="md:hidden h-[42px]" aria-hidden />;
}
